/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BlIcon, BlIconType, inputFormatters } from '@blend/shared-react';
import { colors } from '@blend/consumer-components';
import { AgentDetailsListWrapper } from './AgentDetailsListWrapper';
import { AgentDetailsWrapper } from './AgentDetailsWrapper';
import { AgentDetailsIconWrapper } from './AgentDetailsIconWrapper';
import { ScreenReaderMessage } from '@components/ScreenReaderMessage/ScreenReaderMessage';

export namespace AgentDetails {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    details: AssigneeDetails;
  }
}
export interface AssigneeDetails {
  phoneNumber: string;
  email: string;
  website: string;
  extension?: string;
}

const styles = StyleSheet.create({
  phoneIcon: {
    fontSize: 18,
    paddingLeft: 2,
  },
  emailIcon: {
    fontSize: 22,
    paddingRight: 25,
  },
  websiteIcon: {
    fontSize: 17,
  },
  link: {
    color: colors.reflex,
    fontSize: 16,
  },
});

type AgentDetailsTypes = 'phone' | 'email' | 'website';

interface AgentDetailsInfo {
  iconType: BlIconType;
  className: string;
  a11yText: string;
  url(detail: AssigneeDetails): string;
  text(detail: AssigneeDetails): string;
}

const agentDetailsFormat: Map<AgentDetailsTypes, AgentDetailsInfo> = new Map([
  [
    'phone',
    {
      iconType: 'phone2',
      a11yText: 'Phone number',
      className: css(styles.phoneIcon),
      url: (detail: AssigneeDetails) => `tel:${detail.phoneNumber}${detail.extension ? `;${detail.extension}` : ''}`,
      text: (detail: AssigneeDetails) =>
        `${inputFormatters.PHONE.format(detail.phoneNumber)}${detail.extension ? ` x ${detail.extension}` : ''}`,
    },
  ],
  [
    'email',
    {
      iconType: 'email',
      a11yText: 'Email address',
      className: css(styles.emailIcon),
      url: (detail: AssigneeDetails) => `mailto:${detail.email}`,
      text: (detail: AssigneeDetails) => detail.email,
    },
  ],
  [
    'website',
    {
      iconType: 'link',
      a11yText: 'Website',
      className: css(styles.websiteIcon),
      url: (detail: AssigneeDetails) => detail.website,
      text: (detail: AssigneeDetails) => detail.website,
    },
  ],
]);

export const AgentDetails: React.FC<AgentDetails.Props> = ({ details }) => {
  return (
    <AgentDetailsListWrapper>
      {Array.from(agentDetailsFormat.keys()).map(agentDetailsType => {
        const detailFormat: AgentDetailsInfo = agentDetailsFormat.get(agentDetailsType)!;
        return (
          <li key={detailFormat.iconType}>
            {detailFormat.text(details) && (
              <AgentDetailsWrapper>
                <AgentDetailsIconWrapper>
                  <ScreenReaderMessage>{detailFormat.a11yText}</ScreenReaderMessage>
                  <BlIcon icon={detailFormat.iconType} />
                </AgentDetailsIconWrapper>
                <a className={css(styles.link)} href={detailFormat.url(details)} target="_blank">
                  {detailFormat.text(details)}
                </a>
              </AgentDetailsWrapper>
            )}
          </li>
        );
      })}
    </AgentDetailsListWrapper>
  );
};
